import { useUrlSearchParams } from '@dai/common'
import { ExpandMoreRounded } from '@mui/icons-material'
import { Button, Menu, MenuItem } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { useState } from 'react'

import useT from '@/i18n/useT'

type PreCannedDatePickerProps = {
  startDateKey: string
  endDateKey: string
}

export const PreCannedDatePicker: React.FC<
  PreCannedDatePickerProps
> = props => {
  const { startDateKey, endDateKey } = props
  const {
    state: urlState,
    handle: { handleUpdateSearchParams },
  } = useUrlSearchParams()
  const { t } = useT()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSetDate = (date: Dayjs | null, key: string) => {
    handleUpdateSearchParams({
      [key]: date?.format('YYYY-MM-DD'),
    })
  }

  const selectedDateRange = {
    start: dayjs(urlState[startDateKey] as string),
    end: dayjs(urlState[endDateKey] as string),
  }

  return (
    <>
      <Button
        size="large"
        variant="contained"
        color="secondary"
        aria-owns={anchorEl ? 'simple-menu' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        endIcon={<ExpandMoreRounded />}
        sx={{
          height: 50,
          minWidth: 150,
          maxWidth: 200,
        }}
      >
        {(() => {
          if (
            selectedDateRange.start.isSame(dayjs().startOf('day'), 'day') &&
            selectedDateRange.end.isSame(dayjs(), 'day')
          ) {
            return t('preCannedDatePicker.today')
          } else if (
            selectedDateRange.start.isSame(
              dayjs().subtract(1, 'day').startOf('day'),
              'day',
            ) &&
            selectedDateRange.end.isSame(
              dayjs().subtract(1, 'day').startOf('day'),
              'day',
            )
          ) {
            return t('preCannedDatePicker.yesterday')
          } else if (
            selectedDateRange.start.isSame(
              dayjs().subtract(6, 'day').startOf('day'),
              'day',
            ) &&
            selectedDateRange.end.isSame(dayjs(), 'day')
          ) {
            return t('preCannedDatePicker.last7Days')
          } else if (
            selectedDateRange.start.isSame(
              dayjs().subtract(1, 'week').startOf('week'),
              'day',
            ) &&
            selectedDateRange.end.isSame(
              dayjs().subtract(1, 'week').endOf('week'),
              'day',
            )
          ) {
            return t('preCannedDatePicker.lastWeek')
          } else if (
            selectedDateRange.start.isSame(
              dayjs().subtract(30, 'days'),
              'day',
            ) &&
            selectedDateRange.end.isSame(dayjs(), 'day')
          ) {
            return t('preCannedDatePicker.last30Days')
          } else if (
            selectedDateRange.start.isSame(
              dayjs().subtract(1, 'month').startOf('month'),
              'day',
            ) &&
            selectedDateRange.end.isSame(
              dayjs().subtract(1, 'month').endOf('month'),
              'day',
            )
          ) {
            return t('preCannedDatePicker.previousMonth')
          } else if (
            selectedDateRange.start.isSame(dayjs().startOf('year'), 'day') &&
            selectedDateRange.end.isSame(dayjs(), 'day')
          ) {
            return t('preCannedDatePicker.yearToDate')
          } else {
            return t('preCannedDatePicker.customTimeRange')
          }
        })()}
      </Button>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => {
            handleSetDate(dayjs().startOf('day'), startDateKey)
            handleSetDate(dayjs(), endDateKey)
            handleClose()
          }}
        >
          {t('preCannedDatePicker.today')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            const yesterday = dayjs().subtract(1, 'day').startOf('day')
            handleSetDate(yesterday, startDateKey)
            handleSetDate(yesterday, endDateKey)
            handleClose()
          }}
        >
          {t('preCannedDatePicker.yesterday')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            const startDate = dayjs().subtract(6, 'day').startOf('day')
            handleSetDate(startDate, startDateKey)
            handleSetDate(dayjs(), endDateKey)
            handleClose()
          }}
        >
          {t('preCannedDatePicker.last7Days')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            const startDate = dayjs().subtract(1, 'week').startOf('week')
            handleSetDate(startDate, startDateKey)
            handleSetDate(startDate.endOf('week'), endDateKey)
            handleClose()
          }}
        >
          {t('preCannedDatePicker.lastWeek')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleSetDate(dayjs().subtract(30, 'day'), startDateKey)
            handleSetDate(dayjs(), endDateKey)
            handleClose()
          }}
        >
          {t('preCannedDatePicker.last30Days')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleSetDate(
              dayjs().subtract(1, 'month').startOf('month'),
              startDateKey,
            )
            handleSetDate(
              dayjs().subtract(1, 'month').endOf('month'),
              endDateKey,
            )
            handleClose()
          }}
        >
          {t('preCannedDatePicker.previousMonth')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleSetDate(dayjs().startOf('year'), startDateKey)
            handleSetDate(dayjs(), endDateKey)
            handleClose()
          }}
        >
          {t('preCannedDatePicker.yearToDate')}
        </MenuItem>
      </Menu>
    </>
  )
}
